import React, { memo, useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Waypoint } from 'react-waypoint';
import { FormattedDate } from 'react-intl';

import { usePicker } from 'app/dealer/shared/hooks/usePicker';
import NetworkType from 'app/dealer/account/Network/proptypes/NetworkType.proptypes';

import {
  DEALERS_INSIGHTS_TIME_DIVISIONS,
  DEALERS_INSIGHTS_FEATURES,
} from '../../modules/dealersInsights/api/dealersInsights.api';
import { allPropsAreEqual } from '../../modules/dealersInsights/memoizationFuncs/allPropsAreEqual';

import {
  IconSt,
  DummyPickerWrapper,
  DummyPickerInnerWrapper,
} from './DummyPicker.css';

export const PICKER_TRACKING_ACTIONS = {
  INCREMENT: 'increment',
  INCREMENT_WITH_KEYBOARD: 'increment_with_keyboard',
  DECREMENT: 'decrement',
  DECREMENT_WITH_KEYBOARD: 'decrement_with_keyboard',
};

const DummyPicker = ({
  startDate,
  endDate,
  selectedDate,
  onDateChange,
  timeDivision,
  network,
  feature,
}) => {
  // set if picker is in viewport
  const [pickerInView, setPickerInView] = useState(false);

  const {
    currentMonth,
    currentYear,
    increment,
    decrement,
    intlProps,
    memoCannotGoBack,
    memoCannotGoForward,
  } = usePicker({
    startDate,
    endDate,
    selectedDate,
    timeDivision,
    pickerInView,
    network,
    feature,
  });
  const isInitialMount = useRef(true);

  // call function from parent with month/year
  useEffect(() => {
    // should only execute on change, not on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      onDateChange({ currentMonth, currentYear });
    }
  }, [onDateChange, currentMonth, currentYear]);

  const handleDecrement = useCallback(() => {
    // decrement picker
    decrement({ action: PICKER_TRACKING_ACTIONS.DECREMENT });
  }, [decrement]);

  const handleIncrement = useCallback(() => {
    // increment picker
    increment({ action: PICKER_TRACKING_ACTIONS.INCREMENT });
  }, [increment]);

  const handlePickerInView = useCallback(() => setPickerInView(true), [
    setPickerInView,
  ]);
  const handlePickerOutOfView = useCallback(() => setPickerInView(false), [
    setPickerInView,
  ]);

  return (
    <Waypoint onEnter={handlePickerInView} onLeave={handlePickerOutOfView}>
      <DummyPickerWrapper
        tabIndex={pickerInView ? 0 : -1}
        data-testid={`${feature}-date-picker`}
      >
        <DummyPickerInnerWrapper>
          <IconSt
            disabled={memoCannotGoBack}
            icon="arrow-left-short"
            onClick={handleDecrement}
          />
          <FormattedDate {...intlProps} />
          <IconSt
            disabled={memoCannotGoForward}
            icon="arrow-right-short"
            onClick={handleIncrement}
          />
        </DummyPickerInnerWrapper>
      </DummyPickerWrapper>
    </Waypoint>
  );
};

export const DummyPickerRequiredProps = {
  startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])
    .isRequired,
  endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])
    .isRequired,
  onDateChange: PropTypes.func.isRequired,
  selectedDate: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.number,
  ]).isRequired,
  timeDivision: PropTypes.oneOf(Object.values(DEALERS_INSIGHTS_TIME_DIVISIONS)),
  network: NetworkType,
  feature: PropTypes.oneOf(Object.values(DEALERS_INSIGHTS_FEATURES)),
};

export const DummyPickerDefaultProps = {
  timeDivision: DEALERS_INSIGHTS_TIME_DIVISIONS.MONTH,
  network: null,
};

DummyPicker.propTypes = DummyPickerRequiredProps;
DummyPicker.defaultProps = DummyPickerDefaultProps;

export default memo(DummyPicker, allPropsAreEqual({ memoizeFns: true }));
